import React, { useEffect, useState } from 'react';
import useSWR from 'swr';
import {
  Paper, Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, Chip, Stack, TextField, MenuItem, ToggleButton, ToggleButtonGroup,
  Collapse, IconButton, Tooltip, InputAdornment,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon, KeyboardArrowUp as CollapseIcon, Search as SearchIcon,
} from '@mui/icons-material';
import { fetcher } from 'shared/helpers/axios';

// One distinct colour per activity, used in chips, bars and legends
const ACTIVITY_COLORS = {
  'Writing (proposals & publications)': '#e53935',
  'Funding calls': '#43a047',
  'Consortia': '#1e88e5',
  'Organizations': '#00acc1',
  'Activity feed & posts': '#fb8c00',
  'Messaging': '#8e24aa',
  'Networking': '#3949ab',
  'Meetings & events': '#6d4c41',
  'AI tools': '#d81b60',
  'Profile & settings': '#7cb342',
  'Notifications': '#fdd835',
  'Account & billing': '#546e7a',
  'Sign-in': '#90a4ae',
  'Admin': '#212121',
  'Other': '#bdbdbd',
};
const colorFor = (activity) => ACTIVITY_COLORS[activity] || '#9e9e9e';

const ITEM_LABELS = { proposal: 'Document', call: 'Call', consortium: 'Consortium', organization: 'Organization', user: 'Profile' };

const formatMinutes = (m) => (m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`);
const pct = (share) => `${Math.round(share * 100)}%`;

const BreakdownBar = ({ parts, height = 10 }) => (
  <Box sx={{ display: 'flex', width: '100%', height, borderRadius: 1, overflow: 'hidden', bgcolor: 'action.hover' }}>
    {parts.map((p) => (
      <Tooltip key={p.activity} title={`${p.activity}: ${pct(p.share)}`} arrow>
        <Box sx={{ width: `${p.share * 100}%`, bgcolor: colorFor(p.activity) }} />
      </Tooltip>
    ))}
  </Box>
);

const ActivityChip = ({ part }) => (
  <Chip
    size="small"
    label={`${part.activity} · ${pct(part.share)}`}
    sx={{ bgcolor: colorFor(part.activity), color: '#fff', fontWeight: 600, maxWidth: '100%' }}
  />
);

const ActorRow = ({ row, rank, by }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow hover onClick={() => setOpen((v) => !v)} sx={{ cursor: 'pointer', '& > td': { borderBottom: open ? 'none' : undefined } }}>
        <TableCell padding="checkbox">
          <IconButton size="small" aria-label={open ? 'Hide details' : 'Show details'}>
            {open ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell>{rank}</TableCell>
        <TableCell sx={{ maxWidth: 240 }}>
          <Typography variant="body2" fontWeight={600} noWrap sx={by === 'ip' ? { fontFamily: 'monospace' } : undefined}>
            {row.label}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block">{row.sub}</Typography>
        </TableCell>
        <TableCell sx={{ maxWidth: 260 }}>{row.top_activity && <ActivityChip part={row.top_activity} />}</TableCell>
        <TableCell sx={{ minWidth: 140 }}><BreakdownBar parts={row.breakdown} /></TableCell>
        <TableCell align="right"><strong>{row.score}</strong></TableCell>
        <TableCell align="right">{row.sessions}</TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatMinutes(row.active_minutes)}</TableCell>
        <TableCell align="right">{row.active_days}</TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Typography variant="caption">{row.last_seen ? new Date(row.last_seen).toLocaleString() : '–'}</Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={10} sx={{ py: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' } }}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>What they do</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Activity</TableCell>
                      <TableCell align="right">Share</TableCell>
                      <TableCell align="right">Views</TableCell>
                      <TableCell align="right">Changes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {row.breakdown.map((p) => (
                      <TableRow key={p.activity}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: colorFor(p.activity), flexShrink: 0 }} />
                            {p.activity}
                          </Box>
                        </TableCell>
                        <TableCell align="right">{pct(p.share)}</TableCell>
                        <TableCell align="right">{p.requests - p.writes}</TableCell>
                        <TableCell align="right">{p.writes}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
              <Box>
                <Typography variant="subtitle2" gutterBottom>Most used</Typography>
                {row.top_items.length === 0 && (
                  <Typography variant="body2" color="text.secondary">No specific documents, calls or pages stand out.</Typography>
                )}
                {row.top_items.map((item) => (
                  <Box key={`${item.kind}-${item.id}`} display="flex" alignItems="center" gap={1} mb={0.75}>
                    <Chip size="small" variant="outlined" label={ITEM_LABELS[item.kind] || item.kind} />
                    <Typography variant="body2" noWrap title={item.name}>{item.name}</Typography>
                  </Box>
                ))}
                <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>Traffic</Typography>
                <Typography variant="body2" color="text.secondary">
                  {row.engaged_requests} deliberate requests ({row.writes} changes),{' '}
                  {row.background_requests} background requests out of {row.requests}.
                  {by === 'user' ? ` Seen from ${row.ips} IP address${row.ips === 1 ? '' : 'es'}.` : ` ${row.users} signed-in user${row.users === 1 ? '' : 's'} behind this address.`}
                  {row.first_seen && ` First seen ${new Date(row.first_seen).toLocaleString()}.`}
                </Typography>
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const EngagementPanel = () => {
  const [by, setBy] = useState('user');
  const [days, setDays] = useState('30');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  const { data, error, isLoading } = useSWR(
    `/interact/access-logs/engagement/?by=${by}&days=${days}&limit=50${query ? `&q=${encodeURIComponent(query)}` : ''}`,
    fetcher,
    { keepPreviousData: true },
  );

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2} alignItems={{ sm: 'center' }}>
        <ToggleButtonGroup size="small" exclusive value={by} onChange={(_, v) => v && setBy(v)}
          sx={{ '& .MuiToggleButton-root': { fontSize: '0.85rem', px: 1.5, textTransform: 'none' } }}>
          <ToggleButton value="user">Users</ToggleButton>
          <ToggleButton value="ip">IP addresses</ToggleButton>
        </ToggleButtonGroup>
        <TextField select size="small" label="Period" value={days} onChange={(e) => setDays(e.target.value)} sx={{ minWidth: 150 }}>
          <MenuItem value="1">Last 24 hours</MenuItem>
          <MenuItem value="7">Last 7 days</MenuItem>
          <MenuItem value="30">Last 30 days</MenuItem>
          <MenuItem value="90">Last 90 days</MenuItem>
        </TextField>
        <TextField
          size="small"
          placeholder={by === 'user' ? 'Name or email' : 'IP address or user'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 220 }}
        />
        {isLoading && <CircularProgress size={20} />}
      </Stack>

      <Typography variant="body2" color="text.secondary" mb={2}>
        Score = page views + 3 × changes (saves, posts, invitations…). Background traffic the app sends by
        itself (notification polling, token refresh, editor heartbeats) never counts. A session ends after
        30 minutes without activity; active time caps each pause at 5 minutes.
      </Typography>

      {error && <Alert severity="error">Failed to load engagement data.</Alert>}

      {data && (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1}>
              <Typography variant="subtitle2">What the site is used for</Typography>
              <Typography variant="caption" color="text.secondary">
                {data.actors_total} active {by === 'user' ? 'users' : 'addresses'} · {data.total_requests} requests,{' '}
                {data.total_requests ? pct(data.background_requests / data.total_requests) : '0%'} background
              </Typography>
            </Box>
            <BreakdownBar parts={data.site_breakdown} height={14} />
            <Box display="flex" flexWrap="wrap" columnGap={2} rowGap={0.5} mt={1}>
              {data.site_breakdown.map((p) => (
                <Box key={p.activity} display="flex" alignItems="center" gap={0.75}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: colorFor(p.activity) }} />
                  <Typography variant="caption">{p.activity} {pct(p.share)}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>

          {data.results.length === 0 ? (
            <Alert severity="info">No engaged {by === 'user' ? 'users' : 'addresses'} in this period.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>#</TableCell>
                    <TableCell>{by === 'user' ? 'User' : 'IP address'}</TableCell>
                    <TableCell>Most engaged activity</TableCell>
                    <TableCell>Breakdown</TableCell>
                    <TableCell align="right">Score</TableCell>
                    <TableCell align="right">Sessions</TableCell>
                    <TableCell align="right">Active time</TableCell>
                    <TableCell align="right">Days</TableCell>
                    <TableCell>Last seen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.results.map((row, i) => <ActorRow key={row.key} row={row} rank={i + 1} by={by} />)}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
};

export default EngagementPanel;
